import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../config/supabase'
import Footer from '../components/Footer'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'

const AdminMember = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [members, setMembers] = useState([])
  const [userRole, setUserRole] = useState(null)
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1)
  const [totalMembers, setTotalMembers] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const itemsPerPageOptions = [10, 25, 50, 100]

  // Stats states
  const [stats, setStats] = useState({
    freeMembers: 0,
    vipMembers: 0,
    nearExpiryMembers: 0,
    expiredMembers: 0
  })

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedMember, setSelectedMember] = useState(null)
  const [editForm, setEditForm] = useState({
    display_name: '',
    membership: '',
    expires_at: ''
  })

  // Filter state
  const [filter, setFilter] = useState('all')

  // ตรวจสอบสิทธิ์ admin
  useEffect(() => {
    const checkAdminRole = async () => {
      if (!user) {
        navigate('/')
        return
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('membership')
          .eq('id', user.id)
          .single()

        if (error) throw error

        if (data?.membership !== 'admin') {
          navigate('/')
          return
        }

        setUserRole(data.membership)
      } catch (error) {
        console.error('Error checking admin role:', error)
        navigate('/')
      }
    }

    checkAdminRole()
  }, [user, navigate])

  // ดึงข้อมูลสถิติ
  const fetchStats = async () => {
    try {
      // ดึงข้อมูลสมาชิกทั้งหมด
      const { data: allMembers, error } = await supabase
        .from('profiles')
        .select('*')
        .neq('membership', 'admin')

      if (error) throw error

      const today = new Date()
      let freeCount = 0
      let vipCount = 0
      let nearExpiryCount = 0
      let expiredCount = 0

      allMembers.forEach(member => {
        if (member.membership === 'free') {
          freeCount++
        }
        
        if (member.membership === 'vip') {
          vipCount++
        }

        if (member.expires_at) {
          const expiryDate = new Date(member.expires_at)
          const daysLeft = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24))

          if (daysLeft <= 0) {
            expiredCount++
          } else if (daysLeft < 7) {
            nearExpiryCount++
          }
        }
      })

      setStats({
        freeMembers: freeCount,
        vipMembers: vipCount,
        nearExpiryMembers: nearExpiryCount,
        expiredMembers: expiredCount
      })
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  // Effect for fetching stats
  useEffect(() => {
    if (userRole === 'admin') {
      fetchStats()
    }
  }, [userRole])

  // ดึงข้อมูลสมาชิก
  const fetchMembers = async () => {
    if (userRole !== 'admin') return

    try {
      setLoading(true)
      
      // Calculate pagination range
      const from = (currentPage - 1) * itemsPerPage
      const to = from + itemsPerPage - 1

      let query = supabase
        .from('profiles')
        .select('*', { count: 'exact' })
        .neq('membership', 'admin')
        .order('created_at', { ascending: false })

      // เพิ่มเงื่อนไขการกรอง
      if (filter === 'free') {
        query = query.eq('membership', 'free')
      } else if (filter === 'vip') {
        query = query.eq('membership', 'vip')
      } else if (filter === 'near-expiry') {
        const sevenDaysFromNow = new Date()
        sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7)
        query = query
          .gt('expires_at', new Date().toISOString())
          .lt('expires_at', sevenDaysFromNow.toISOString())
      } else if (filter === 'expired') {
        query = query
          .lt('expires_at', new Date().toISOString())
      }

      // เพิ่ม pagination
      const { data, count, error } = await query
        .range(from, to)

      if (error) throw error

      setMembers(data || [])
      setTotalMembers(count || 0)
    } catch (error) {
      console.error('Error fetching members:', error)
    } finally {
      setLoading(false)
    }
  }

  // Effect for fetching members
  useEffect(() => {
    if (userRole === 'admin') {
      setCurrentPage(1) // รีเซ็ตหน้าเมื่อเปลี่ยนฟิลเตอร์
      fetchMembers()
    }
  }, [userRole, currentPage, itemsPerPage, filter])

  // เปิด Modal แก้ไข
  const handleEdit = (member) => {
    setSelectedMember(member)
    setEditForm({
      display_name: member.display_name || '',
      membership: member.membership || 'free',
      expires_at: member.expires_at || ''
    })
    setShowEditModal(true)
  }

  // บันทึกการแก้ไข
  const handleSave = async () => {
    try {
      setLoading(true)

      // อัพเดทข้อมูลในฐานข้อมูล
      const { error } = await supabase
        .from('profiles')
        .update({
          display_name: editForm.display_name,
          membership: editForm.membership,
          expires_at: editForm.expires_at || null
        })
        .eq('id', selectedMember.id)

      if (error) throw error

      // อัพเดทข้อมูลในตาราง
      setMembers(members.map(member => 
        member.id === selectedMember.id 
          ? { ...member, ...editForm }
          : member
      ))

      setShowEditModal(false)
      setSelectedMember(null)
      alert('บันทึกการเปลี่ยนแปลงเรียบร้อย')
    } catch (error) {
      console.error('Error updating member:', error)
      alert('เกิดข้อผิดพลาดในการบันทึก')
    } finally {
      setLoading(false)
    }
  }

  // ลบสมาชิก
  const handleDelete = async (memberId) => {
    if (!window.confirm('คุณแน่ใจหรือไม่ที่จะลบสมาชิกนี้?')) return

    try {
      setLoading(true)

      // ลบข้อมูลจากฐานข้อมูล
      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', memberId)

      if (error) throw error

      // ลบข้อมูลจากตาราง
      setMembers(members.filter(member => member.id !== memberId))
      alert('ลบสมาชิกเรียบร้อย')
    } catch (error) {
      console.error('Error deleting member:', error)
      alert('เกิดข้อผิดพลาดในการลบ')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>จัดการสมาชิก - AI Unlocked</title>
      </Helmet>

      <main className="max-w-6xl mx-auto p-6">
        {/* Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {/* สมาชิกฟรี */}
          <div 
            className={`bg-gradient-to-br from-gray-500/10 to-gray-600/10 rounded-lg p-6 border ${
              filter === 'free' 
                ? 'border-gray-400 shadow-lg shadow-gray-500/20' 
                : 'border-gray-500/20'
            } cursor-pointer transition-all duration-200 hover:border-gray-400`}
            onClick={() => setFilter(filter === 'free' ? 'all' : 'free')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">สมาชิกทั่วไป</p>
                <p className="text-2xl font-bold text-gray-300 mt-1">
                  {stats.freeMembers.toLocaleString()} คน
                </p>
              </div>
              <div className={`p-3 ${filter === 'free' ? 'bg-gray-400/20' : 'bg-gray-500/20'} rounded-lg`}>
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            </div>
          </div>

          {/* สมาชิก VIP */}
          <div 
            className={`bg-gradient-to-br from-green-500/10 to-green-600/10 rounded-lg p-6 border ${
              filter === 'vip' 
                ? 'border-green-400 shadow-lg shadow-green-500/20' 
                : 'border-green-500/20'
            } cursor-pointer transition-all duration-200 hover:border-green-400`}
            onClick={() => setFilter(filter === 'vip' ? 'all' : 'vip')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">สมาชิก VIP</p>
                <p className="text-2xl font-bold text-green-400 mt-1">
                  {stats.vipMembers.toLocaleString()} คน
                </p>
              </div>
              <div className={`p-3 ${filter === 'vip' ? 'bg-green-400/20' : 'bg-green-500/20'} rounded-lg`}>
                <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
            </div>
          </div>

          {/* สมาชิกใกล้หมดอายุ */}
          <div 
            className={`bg-gradient-to-br from-yellow-500/10 to-yellow-600/10 rounded-lg p-6 border ${
              filter === 'near-expiry' 
                ? 'border-yellow-400 shadow-lg shadow-yellow-500/20' 
                : 'border-yellow-500/20'
            } cursor-pointer transition-all duration-200 hover:border-yellow-400`}
            onClick={() => setFilter(filter === 'near-expiry' ? 'all' : 'near-expiry')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">ใกล้หมดอายุ</p>
                <p className="text-2xl font-bold text-yellow-400 mt-1">
                  {stats.nearExpiryMembers.toLocaleString()} คน
                </p>
              </div>
              <div className={`p-3 ${filter === 'near-expiry' ? 'bg-yellow-400/20' : 'bg-yellow-500/20'} rounded-lg`}>
                <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* สมาชิกหมดอายุ */}
          <div 
            className={`bg-gradient-to-br from-red-500/10 to-red-600/10 rounded-lg p-6 border ${
              filter === 'expired' 
                ? 'border-red-400 shadow-lg shadow-red-500/20' 
                : 'border-red-500/20'
            } cursor-pointer transition-all duration-200 hover:border-red-400`}
            onClick={() => setFilter(filter === 'expired' ? 'all' : 'expired')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">หมดอายุแล้ว</p>
                <p className="text-2xl font-bold text-red-400 mt-1">
                  {stats.expiredMembers.toLocaleString()} คน
                </p>
              </div>
              <div className={`p-3 ${filter === 'expired' ? 'bg-red-400/20' : 'bg-red-500/20'} rounded-lg`}>
                <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-900 rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-white">
              จัดการสมาชิก
              {filter !== 'all' && (
                <span className="ml-2 text-sm font-normal text-gray-400">
                  {filter === 'free' && '(สมาชิกทั่วไป)'}
                  {filter === 'vip' && '(สมาชิก VIP)'}
                  {filter === 'near-expiry' && '(ใกล้หมดอายุ)'}
                  {filter === 'expired' && '(หมดอายุแล้ว)'}
                </span>
              )}
            </h1>
            
            {/* Items per page selector */}
            <div className="flex items-center gap-2">
              <span className="text-gray-400">แสดง:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value))
                  setCurrentPage(1)
                }}
                className="bg-gray-700 text-white px-3 py-1 rounded-lg"
              >
                {itemsPerPageOptions.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <span className="text-gray-400">รายการต่อหน้า</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    วันที่สมัคร
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    อีเมล
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    ชื่อที่แสดง
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    ประเภทสมาชิก
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    วันหมดอายุ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    การจัดการ
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {members.map((member) => (
                  <tr key={member.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {new Date(member.created_at).toLocaleDateString('th-TH', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {member.email || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {member.display_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        member.membership === 'admin' 
                          ? 'bg-red-900 text-red-300'
                          : member.membership === 'vip'
                          ? 'bg-green-900 text-green-300'
                          : 'bg-gray-800 text-gray-300'
                      }`}>
                        {member.membership === 'admin' 
                          ? 'ผู้ดูแลระบบ' 
                          : member.membership === 'vip'
                          ? 'สมาชิก VIP'
                          : 'สมาชิกทั่วไป'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {member.expires_at 
                        ? (() => {
                            const expiryDate = new Date(member.expires_at)
                            const today = new Date()
                            const daysLeft = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24))
                            
                            return (
                              <span className={`${daysLeft < 7 ? 'text-red-400' : 'text-gray-300'}`}>
                                {new Date(member.expires_at).toLocaleDateString('th-TH', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                                {daysLeft < 7 && daysLeft > 0 && (
                                  <span className="ml-2 text-xs bg-red-900/50 text-red-300 px-2 py-0.5 rounded-full">
                                    เหลือ {daysLeft} วัน
                                  </span>
                                )}
                                {daysLeft <= 0 && (
                                  <span className="ml-2 text-xs bg-red-900/50 text-red-300 px-2 py-0.5 rounded-full">
                                    หมดอายุแล้ว
                                  </span>
                                )}
                              </span>
                            )
                          })()
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      <button
                        onClick={() => handleEdit(member)}
                        className="text-yellow-400 hover:text-yellow-300 mr-2"
                      >
                        แก้ไข
                      </button>
                      {member.membership !== 'admin' && (
                        <button
                          onClick={() => handleDelete(member.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          ลบ
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-6 flex justify-between items-center">
            <div className="text-sm text-gray-400">
              แสดง {((currentPage - 1) * itemsPerPage) + 1} ถึง {Math.min(currentPage * itemsPerPage, totalMembers)} จาก {totalMembers} รายการ
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50 hover:bg-gray-600"
              >
                ก่อนหน้า
              </button>
              {/* Page numbers */}
              <div className="flex gap-1">
                {[...Array(Math.min(5, Math.ceil(totalMembers / itemsPerPage)))].map((_, i) => {
                  const pageNum = i + 1
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-4 py-2 rounded-lg ${
                        currentPage === pageNum
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-700 text-white hover:bg-gray-600'
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                })}
                {Math.ceil(totalMembers / itemsPerPage) > 5 && (
                  <span className="px-4 py-2 text-gray-400">...</span>
                )}
              </div>
              <button
                onClick={() => setCurrentPage(p => Math.min(p + 1, Math.ceil(totalMembers / itemsPerPage)))}
                disabled={currentPage >= Math.ceil(totalMembers / itemsPerPage)}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50 hover:bg-gray-600"
              >
                ถัดไป
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">แก้ไขข้อมูลสมาชิก</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  ชื่อที่แสดง
                </label>
                <input
                  type="text"
                  value={editForm.display_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, display_name: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  ประเภทสมาชิก
                </label>
                <select
                  value={editForm.membership}
                  onChange={(e) => setEditForm(prev => ({ ...prev, membership: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                >
                  <option value="free">สมาชิกทั่วไป</option>
                  <option value="vip">สมาชิก VIP</option>
                  {selectedMember?.membership === 'admin' && (
                    <option value="admin">ผู้ดูแลระบบ</option>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  วันหมดอายุ
                </label>
                <input
                  type="date"
                  value={editForm.expires_at?.split('T')[0] || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, expires_at: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50"
              >
                บันทึก
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  )
}

export default AdminMember
